// Shared ownership / read-state helpers for customer conversations.
//
// The sidebar counters (nav-webchat.tsx) and the conversation type selector both
// need to agree on two questions: "is this conversation mine?" and "is it unread
// for me?". Keep the answers here so the badge, the counts and the list can't drift.

type IdLike = string | { _id?: unknown; toString(): string } | null | undefined;

// Ids arrive as plain strings, ObjectId-ish values with toString(), or populated
// documents (e.g. department._users is populated with user records). Normalise all
// of them to a string id so comparisons are shape-independent.
const toId = (value: IdLike): string => {
  if (value == null) {
    return '';
  }
  if (typeof value === 'object' && '_id' in value && value._id != null) {
    return String(value._id);
  }
  return String(value);
};

export interface AssignableConversation {
  _assignees?: IdLike[] | null;
  _assignee?: IdLike;
  _department?: IdLike;
}

export interface ReadableConversation {
  isRead?: boolean;
  _readByUsers?: IdLike[] | null;
}

export interface DepartmentLike {
  _id: IdLike;
  _users?: IdLike[] | null;
}

export interface ReadStateUser {
  _id: IdLike;
  hasOwnIsReadIndicators?: boolean;
}

/**
 * Ids of every department the user belongs to. Feed the result to isAssignedToUser.
 */
export const getUserDepartmentIds = (
  departments: DepartmentLike[] | null | undefined,
  userId: IdLike,
): string[] => {
  const me = toId(userId);
  if (!me || !departments?.length) {
    return [];
  }

  return departments
    .filter((department) => (department._users || []).some((member) => toId(member) === me))
    .map((department) => toId(department._id))
    .filter(Boolean);
};

/**
 * Direct assignment only. _assignees (array) is primary; _assignee (single) is the
 * deprecated fallback and is only consulted when _assignees is empty.
 */
export const isDirectlyAssignedToUser = (
  convo: AssignableConversation,
  userId: IdLike,
): boolean => {
  const me = toId(userId);
  if (!me) {
    return false;
  }
  if (convo._assignees && convo._assignees.length > 0) {
    return convo._assignees.some((assignee) => toId(assignee) === me);
  }
  return toId(convo._assignee) === me;
};

/**
 * Assigned to the user directly, or to a department the user is a member of.
 * This mirrors the server's contributor access rule (assignee OR assignees OR
 * department membership). Pass getUserDepartmentIds(state.departments, user._id)
 * as userDepartmentIds; omit it to check direct assignment only.
 */
export const isAssignedToUser = (
  convo: AssignableConversation,
  userId: IdLike,
  userDepartmentIds: string[] = [],
): boolean => {
  if (isDirectlyAssignedToUser(convo, userId)) {
    return true;
  }
  const department = toId(convo._department);
  return !!department && userDepartmentIds.includes(department);
};

/**
 * No assignees, no legacy assignee and no department.
 */
export const isUnassigned = (convo: AssignableConversation): boolean => {
  const hasAssignees = !!convo._assignees && convo._assignees.length > 0;
  const hasLegacyAssignee = !!convo._assignee;
  const hasDepartment = !!convo._department;
  return !hasAssignees && !hasLegacyAssignee && !hasDepartment;
};

/**
 * Whether the conversation should show as unread for this user.
 *
 * Users with hasOwnIsReadIndicators track read state per user via _readByUsers and
 * only fall back to the business-wide isRead flag when _readByUsers is absent.
 * Everyone else uses the business-wide isRead flag, unchanged.
 */
export const isUnreadForUser = (
  convo: ReadableConversation,
  user: ReadStateUser | null | undefined,
): boolean => {
  if (user?.hasOwnIsReadIndicators && Array.isArray(convo._readByUsers)) {
    const me = toId(user._id);
    return !convo._readByUsers.some((reader) => toId(reader) === me);
  }
  return !convo.isRead;
};
